import React from "react";
import { X, Copy as CopyIcon } from "lucide-react";
import { toast } from "react-hot-toast";
import { useModal } from "../../hooks/useModal";
import { useLanguage } from "../../contexts/LanguageContext";

// Stable hook contract surfaced in the modal. Single source of truth for the
// copy/paste examples below — keep these strings in sync with the widget CSS
// and the launcherCustomCSS validator (src/lib/launcherCssValidator.js).
const LAUNCHER_CLASSES = [
  [".kodly-ai-agent-launcher", "root button"],
  [".kodly-ai-agent-launcher__avatar", "image container"],
  [".kodly-ai-agent-launcher__title", "text label"],
  [".kodly-ai-agent-launcher__badge", "unread count"],
];

const LAUNCHER_DATA_ATTRS = [
  ['[data-shape="pill" | "circle"]', "shape"],
  ['[data-corner="bottom-right" | "bottom-left"]', "corner"],
  ['[data-animation="none" | "float" | "pulse" | "bounce"]', "animation"],
];

const LAUNCHER_CSS_VARS = [
  "--kodly-ai-agent-launcher-bg",
  "--kodly-ai-agent-launcher-text",
  "--kodly-ai-agent-launcher-size",
  "--kodly-ai-agent-launcher-offset-x",
  "--kodly-ai-agent-launcher-offset-y",
];

const LAUNCHER_KEYFRAMES = [
  "@keyframes kodly-ai-agent-float",
  "@keyframes kodly-ai-agent-pulse",
  "@keyframes kodly-ai-agent-bounce",
];

const PANEL_CLASSES = [
  [".kodly-ai-agent-panel", "main panel container"],
  [".kodly-ai-agent-panel-header", "header bar"],
  [".kodly-ai-agent-message-bubble", "message bubble (any role)"],
  [".kodly-ai-agent-message-body", "message text content"],
];

const PANEL_CSS_VARS = [
  "--kodly-ai-agent-primary",
  "--kodly-ai-agent-secondary",
];

const LAUNCHER_EXAMPLES = [
  {
    titleKey: "infoExampleFloatGlow",
    titleFallback: "Float animation + glow",
    code:
      ".kodly-ai-agent-launcher {\n" +
      "  filter: drop-shadow(0 0 10px rgba(0, 255, 195, 0.5));\n" +
      "  background: transparent;\n" +
      "}\n" +
      ".kodly-ai-agent-launcher[data-animation=\"float\"] {\n" +
      "  animation-duration: 4s;\n" +
      "}",
  },
  {
    titleKey: "infoExampleGlass",
    titleFallback: "Glassmorphism",
    code:
      ".kodly-ai-agent-launcher {\n" +
      "  background: rgba(255, 255, 255, 0.15);\n" +
      "  backdrop-filter: blur(8px);\n" +
      "  border: 1px solid rgba(255, 255, 255, 0.2);\n" +
      "}",
  },
  {
    titleKey: "infoExampleHoverScale",
    titleFallback: "Hover scale + shadow lift",
    code:
      ".kodly-ai-agent-launcher {\n" +
      "  transition: transform 0.2s ease, box-shadow 0.2s ease;\n" +
      "}\n" +
      ".kodly-ai-agent-launcher:hover {\n" +
      "  transform: scale(1.08);\n" +
      "  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);\n" +
      "}",
  },
];

// Panel examples deliberately include @font-face — allowed in panel CSS because
// it runs inside the widget iframe (no host-page font-loading concern). The
// launcher list above must NOT include @font-face: launcherCustomCSS rejects it.
const PANEL_EXAMPLES = [
  {
    titleKey: "infoExampleHeaderGradient",
    titleFallback: "Custom header background",
    code:
      ".kodly-ai-agent-panel-header {\n" +
      "  background: linear-gradient(135deg, #1F1F1F, #00B088);\n" +
      "}",
  },
  {
    titleKey: "infoExampleBubbleShape",
    titleFallback: "Message bubble shape",
    code:
      ".kodly-ai-agent-message-bubble {\n" +
      "  border-radius: 8px;\n" +
      "}",
  },
  {
    titleKey: "infoExampleCustomFont",
    titleFallback: "Custom font for messages",
    code:
      "@font-face {\n" +
      "  font-family: 'TenantBrand';\n" +
      "  src: url('https://cdn.tenant.com/font.woff2') format('woff2');\n" +
      "}\n" +
      ".kodly-ai-agent-message-body {\n" +
      "  font-family: 'TenantBrand', sans-serif;\n" +
      "}",
  },
];

const SectionHeader = ({ children }) => (
  <h3
    className="text-xs font-medium uppercase tracking-wide mb-2"
    style={{ color: "var(--color-text-muted)" }}
  >
    {children}
  </h3>
);

const CodeInline = ({ children }) => (
  <code
    className="px-1 py-0.5 rounded"
    style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "12px",
      backgroundColor: "var(--color-surface-raised)",
      color: "var(--color-text-primary)",
    }}
  >
    {children}
  </code>
);

const TwoColList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map(([key, desc]) => (
      <li key={key} className="text-sm flex items-start gap-2 flex-wrap">
        <CodeInline>{key}</CodeInline>
        <span style={{ color: "var(--color-text-secondary)" }}>— {desc}</span>
      </li>
    ))}
  </ul>
);

const SingleColList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((key) => (
      <li key={key} className="text-sm">
        <CodeInline>{key}</CodeInline>
      </li>
    ))}
  </ul>
);

const ExampleSnippet = ({ title, code, copyLabel, copiedMessage }) => {
  const handleCopy = () => {
    // Fire-and-forget: clipboard rejection is rare and non-blocking.
    // Worst case the user doesn't see the toast and re-copies manually.
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(copiedMessage))
      .catch(() => {
        /* swallow — no actionable recovery */
      });
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="text-sm font-medium"
          style={{ color: "var(--color-text-primary)" }}
        >
          {title}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded hover:opacity-80"
          style={{
            color: "var(--color-text-secondary)",
            border: "1px solid var(--color-border)",
            backgroundColor: "var(--color-surface-raised)",
          }}
        >
          <CopyIcon size={12} />
          {copyLabel}
        </button>
      </div>
      <pre
        className="p-3 rounded-lg overflow-x-auto"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "12px",
          lineHeight: "1.5",
          backgroundColor: "var(--color-surface-raised)",
          color: "var(--color-text-primary)",
          border: "1px solid var(--color-border)",
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
};

export default function LauncherCustomizationInfoModal({
  isOpen,
  surface,
  onClose,
}) {
  const { translate } = useLanguage();
  const modalRef = useModal(isOpen, onClose);
  if (!isOpen) return null;
  const isLauncher = surface === "launcher";

  const title = isLauncher
    ? translate("infoLauncherTitle") || "Launcher customization"
    : translate("infoPanelTitle") || "Panel customization";

  const copyLabel = translate("infoCopy") || "Copy";
  const copiedMessage = translate("infoCopied") || "Copied!";
  const closeLabel = translate("infoClose") || "Close";

  const examples = isLauncher ? LAUNCHER_EXAMPLES : PANEL_EXAMPLES;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="card rounded-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "var(--color-surface)" }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <h2
              className="text-lg font-semibold"
              style={{ color: "var(--color-text-primary)" }}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="p-1 rounded hover:opacity-80"
              style={{ color: "var(--color-text-secondary)" }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          {isLauncher ? (
            <div className="space-y-5">
              <section>
                <SectionHeader>
                  {translate("infoStableClasses") || "Stable classes"}
                </SectionHeader>
                <TwoColList items={LAUNCHER_CLASSES} />
              </section>

              <section>
                <SectionHeader>
                  {translate("infoDataAttributes") || "Data attributes"}
                </SectionHeader>
                <TwoColList items={LAUNCHER_DATA_ATTRS} />
              </section>

              <section>
                <SectionHeader>
                  {translate("infoCssVars") || "CSS custom properties"}
                </SectionHeader>
                <SingleColList items={LAUNCHER_CSS_VARS} />
              </section>

              <section>
                <SectionHeader>
                  {translate("infoKeyframes") ||
                    "Animation keyframes (redefinable)"}
                </SectionHeader>
                <SingleColList items={LAUNCHER_KEYFRAMES} />
              </section>

              <p
                className="text-sm p-3 rounded-lg"
                style={{
                  color: "var(--color-text-secondary)",
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                {translate("infoLauncherScopingNote") ||
                  "Top-level selectors must start with .kodly-ai-agent-launcher. Allowed at-rules: @keyframes, @media, @supports. Anything else is blocked."}
              </p>

              <section>
                <SectionHeader>
                  {translate("infoCssExamples") || "Examples"}
                </SectionHeader>
                {examples.map((ex) => (
                  <ExampleSnippet
                    key={ex.titleKey}
                    title={translate(ex.titleKey) || ex.titleFallback}
                    code={ex.code}
                    copyLabel={copyLabel}
                    copiedMessage={copiedMessage}
                  />
                ))}
              </section>
            </div>
          ) : (
            <div className="space-y-5">
              <p
                className="text-sm p-3 rounded-lg"
                style={{
                  color: "var(--color-text-secondary)",
                  backgroundColor: "var(--color-surface-raised)",
                  border: "1px solid var(--color-border)",
                }}
              >
                {translate("infoPanelHintsNote") ||
                  "These hooks are stable today but may change with widget updates. For long-term stability, prefer the structured theme fields above (primary/secondary colors, logo, copy)."}
              </p>

              <section>
                <SectionHeader>
                  {translate("infoStableClasses") || "Stable classes"}
                </SectionHeader>
                <TwoColList items={PANEL_CLASSES} />
              </section>

              <section>
                <SectionHeader>
                  {translate("infoCssVars") || "CSS custom properties"}
                </SectionHeader>
                <SingleColList items={PANEL_CSS_VARS} />
              </section>

              <section>
                <SectionHeader>
                  {translate("infoCssExamples") || "Examples"}
                </SectionHeader>
                {examples.map((ex) => (
                  <ExampleSnippet
                    key={ex.titleKey}
                    title={translate(ex.titleKey) || ex.titleFallback}
                    code={ex.code}
                    copyLabel={copyLabel}
                    copiedMessage={copiedMessage}
                  />
                ))}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
